import { useEffect, useState } from "react";
import {
  AlertTriangle,
  BellRing,
  Bike,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  ReceiptText,
  Wallet,
} from "lucide-react";
import { Button } from "@veorest/ui";
import { formatCurrency } from "@veorest/utils";
import { SegmentStrip } from "../components/data/SegmentStrip";
import { StatsStrip } from "../components/data/StatsStrip";
import { formatShortDateTime } from "../lib/partnerReports";
import { usePartnerDriverWalletsV2, usePartnerWalletV2 } from "../hooks/usePartnerWallets";
import { walletTxDescriptionKey } from "../lib/partnerWallets";
import { useI18n, type TranslationKey } from "../i18n";

const TX_PAGE_SIZE = 10;

type WalletTab = "transactions" | "fleet";

const TX_TYPES = new Set([
  "topup",
  "platform_fee",
  "adjustment",
  "refund",
]);

function txTypeKey(type: string): TranslationKey {
  const normalized = TX_TYPES.has(type) ? type : "other";
  return `walletV2.txType.${normalized}` as TranslationKey;
}

/**
 * Partner Wallet (v2) — consolidated billing wallet.
 * Fee-only rail: 250 IQD platform fee per completed order + delivery-fee
 * settlements. Food/order values never touch the wallet.
 */
export default function WalletPage() {
  const { t, locale } = useI18n();
  const walletQuery = usePartnerWalletV2();
  const fleetQuery = usePartnerDriverWalletsV2();

  const snapshot = walletQuery.data;
  const wallet = snapshot?.wallet;
  const money = (v: number, currency = wallet?.currency ?? "IQD") =>
    formatCurrency(v, currency, locale);
  const signed = (v: number) => `${v > 0 ? "+" : v < 0 ? "−" : ""}${money(Math.abs(v))}`;
  const txDescription = (description: string | null) => {
    const key = walletTxDescriptionKey(description);
    return key ? t(key) : description || t("common.dash");
  };

  const balance = wallet?.balance ?? 0;
  const limit = wallet?.negative_credit_limit ?? 0;
  const threshold = wallet?.alert_threshold_iqd ?? 10000;
  const blocked = balance < limit;
  const low = !blocked && balance <= threshold;

  const fleet = fleetQuery.data ?? [];
  const fleetDues = fleet.reduce((sum, d) => sum + d.payout_due, 0);
  const fleetCash = fleet.reduce((sum, d) => sum + d.cash_held, 0);

  const txs = snapshot?.transactions ?? [];
  const [txPage, setTxPage] = useState(0);
  useEffect(() => {
    setTxPage(0);
  }, [txs.length]);
  const txTotal = txs.length;
  const txFrom = txTotal === 0 ? 0 : txPage * TX_PAGE_SIZE + 1;
  const txTo = Math.min((txPage + 1) * TX_PAGE_SIZE, txTotal);
  const txPageCount = Math.max(1, Math.ceil(txTotal / TX_PAGE_SIZE));
  const txRows = txs.slice(txPage * TX_PAGE_SIZE, (txPage + 1) * TX_PAGE_SIZE);

  const [tab, setTab] = useState<WalletTab>("transactions");
  const tabs = [
    {
      key: "transactions" as const,
      label: t("walletV2.transactionsTitle"),
      icon: ReceiptText,
    },
    { key: "fleet" as const, label: t("walletV2.fleetTitle"), icon: Bike },
  ];
  const statusLabel = blocked
    ? t("walletV2.statusBlocked")
    : low
      ? t("walletV2.statusLow")
      : t("walletV2.statusHealthy");
  const balanceCaption = wallet?.updated_at
    ? `${statusLabel} · ${t("walletV2.updatedAt", {
        time: formatShortDateTime(wallet.updated_at),
      })}`
    : statusLabel;

  return (
    <div className="menu-page menu-page--wide">
      <header className="menu-page__header overview-page__header">
        <div className="min-w-0">
          <p className="menu-page__eyebrow">{t("nav.wallet")}</p>
          <h1 className="menu-page__title overview-page__title">
            {t("walletV2.title")}
          </h1>
          <p className="menu-page__subtitle overview-page__subtitle">
            {t("walletV2.hint")}
          </p>
        </div>
        <div className="menu-page__header-actions overview-page__header-actions">
          <div className="overview-date-live">
            <span className="overview-date-live__dot" aria-hidden />
            {t("overview.updatedLive")}
          </div>
        </div>
      </header>

      {blocked ? (
        <div
          className="flex items-start gap-3 rounded-xl border px-4 py-3"
          style={{
            borderColor: "color-mix(in srgb, var(--danger) 30%, transparent)",
            background: "var(--danger-light)",
            color: "var(--danger)",
          }}
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <p className="text-sm font-semibold">{t("walletV2.blockedBanner")}</p>
        </div>
      ) : low ? (
        <div
          className="flex items-start gap-3 rounded-xl border px-4 py-3"
          style={{
            borderColor: "color-mix(in srgb, var(--warning) 30%, transparent)",
            background: "var(--warning-light)",
            color: "var(--warning)",
          }}
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <p className="text-sm font-semibold">{t("walletV2.lowBanner")}</p>
        </div>
      ) : null}

      <StatsStrip
        columns={3}
        loading={walletQuery.isLoading}
        tiles={[
          {
            key: "balance",
            label: t("walletV2.balanceLabel"),
            value: wallet ? money(balance) : "—",
            icon: Wallet,
            tone: blocked ? "ink" : low ? "info" : "success",
            caption: balanceCaption,
          },
          {
            key: "limit",
            label: t("walletV2.creditLimit"),
            value: wallet ? money(limit) : "—",
            icon: CreditCard,
            tone: "info",
            caption: t("walletV2.creditLimitHint"),
          },
          {
            key: "threshold",
            label: t("walletV2.alertThreshold"),
            value: wallet ? money(threshold) : "—",
            icon: BellRing,
            tone: "accent",
            caption: t("walletV2.alertThresholdHint"),
          },
        ]}
      />

      <p className="text-xs text-[var(--text-muted)]">{t("walletV2.feeOnlyNote")}</p>

      {snapshot?.totals && snapshot.totals.tx_count > 0 ? (
        <p className="text-xs font-semibold text-[var(--text-secondary)]">
          {t("walletV2.totalsLine", {
            fees: money(Math.abs(snapshot.totals.fees_total)),
            topups: money(snapshot.totals.topups_total),
            count: snapshot.totals.tx_count,
          })}
        </p>
      ) : null}

      <SegmentStrip
        value={tab}
        onChange={setTab}
        options={tabs}
        ariaLabel={t("walletV2.title")}
      />

      {tab === "transactions" ? (
      <section className="menu-panel">
        <div className="menu-panel__body space-y-3">
          <h3 className="text-sm font-extrabold uppercase tracking-wide text-[var(--text-secondary)]">
            {t("walletV2.transactionsTitle")}
          </h3>
          {walletQuery.isLoading ? (
            <div className="h-24 rounded-xl skeleton" />
          ) : txTotal === 0 ? (
            <p className="text-sm text-[var(--text-muted)]">{t("walletV2.empty")}</p>
          ) : (
            <>
              <div className="report-table-wrap">
              <table className="table-flat">
                <thead>
                  <tr>
                    <th>{t("walletV2.colDate")}</th>
                    <th>{t("walletV2.colType")}</th>
                    <th>{t("walletV2.colDescription")}</th>
                    <th className="text-end">{t("walletV2.colAmount")}</th>
                    <th className="text-end">{t("walletV2.colBalanceAfter")}</th>
                  </tr>
                </thead>
                <tbody>
                  {txRows.map((tx) => (
                    <tr key={tx.id}>
                      <td className="whitespace-nowrap text-xs">
                        {formatShortDateTime(tx.created_at)}
                      </td>
                      <td className="text-xs font-bold">
                        {t(txTypeKey(tx.type))}
                      </td>
                      <td className="max-w-[16rem] truncate text-xs text-[var(--text-secondary)]">
                        {txDescription(tx.description)}
                      </td>
                      <td
                        className={`text-end tabular text-sm font-bold ${
                          tx.amount < 0 ? "text-red-600" : "text-[var(--accent)]"
                        }`}
                      >
                        {signed(tx.amount)}
                      </td>
                      <td className="text-end tabular text-xs">
                        {money(tx.balance_after)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
              <p className="text-xs text-[var(--text-muted)]">
                {t("clientOrders.showing", {
                  from: txFrom,
                  to: txTo,
                  total: txTotal,
                })}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="secondary"
                  onClick={() => setTxPage((p) => Math.max(0, p - 1))}
                  disabled={txPage === 0}
                  className="min-h-9 gap-1 px-3"
                >
                  <ChevronLeft className="h-4 w-4 rtl:rotate-180" />
                  {t("clientOrders.prev")}
                </Button>
                <span className="text-xs font-bold text-[var(--text-secondary)]">
                  {txPage + 1} / {txPageCount}
                </span>
                <Button
                  variant="secondary"
                  onClick={() => setTxPage((p) => (txTo < txTotal ? p + 1 : p))}
                  disabled={txTo >= txTotal}
                  className="min-h-9 gap-1 px-3"
                >
                  {t("clientOrders.next")}
                  <ChevronRight className="h-4 w-4 rtl:rotate-180" />
                </Button>
              </div>
            </div>
            </>
          )}
        </div>
      </section>
      ) : (
        <section className="menu-panel">
        <div className="menu-panel__body space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="flex items-center gap-2 text-sm font-extrabold uppercase tracking-wide text-[var(--text-secondary)]">
              <Bike className="h-4 w-4" /> {t("walletV2.fleetTitle")}
            </h3>
            {fleet.length > 0 ? (
              <p className="text-xs font-semibold text-[var(--text-secondary)]">
                {t("walletV2.fleetTotals", {
                  cash: money(fleetCash),
                  due: money(fleetDues),
                })}
              </p>
            ) : null}
          </div>

          {fleetQuery.isLoading ? (
            <div className="h-24 rounded-xl skeleton" />
          ) : fleet.length === 0 ? (
            <p className="text-sm text-[var(--text-muted)]">{t("walletV2.fleetEmpty")}</p>
          ) : (
            <div className="report-table-wrap">
              <table className="table-flat">
                <thead>
                  <tr>
                    <th>{t("walletV2.colDriver")}</th>
                    <th className="text-end">{t("walletV2.colBalance")}</th>
                    <th className="text-end">{t("walletV2.colCashHeld")}</th>
                    <th className="text-end">{t("walletV2.colPayoutDue")}</th>
                  </tr>
                </thead>
                <tbody>
                  {fleet.map((row) => (
                    <tr key={row.driver_id}>
                      <td className="text-sm font-semibold">{row.name}</td>
                      <td
                        className={`text-end tabular text-sm font-bold ${
                          row.balance > 0
                            ? "text-[var(--accent)]"
                            : row.balance < 0
                              ? "text-red-600"
                              : ""
                        }`}
                      >
                        {signed(row.balance)}
                      </td>
                      <td className="text-end tabular text-sm">
                        {row.cash_held > 0 ? money(row.cash_held) : "—"}
                      </td>
                      <td className="text-end tabular text-sm">
                        {row.payout_due > 0 ? money(row.payout_due) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <p className="text-xs text-[var(--text-muted)]">{t("walletV2.fleetNote")}</p>
        </div>
        </section>
      )}
    </div>
  );
}
